import { EM_DASH } from '../constants.js'

export type RefrigerantCategory =
    | 'HCFC'
    | 'HFC'
    | 'HFC Blend'
    | 'HFO'
    | 'HFO/HFC Blend'
    | 'Natural'

export type RegulatoryStatus =
    | 'Active'
    | 'Phasing Down'
    | 'Phased Out'
    | 'Transitional'
    | 'Future-Proof'

export type SafetyClass = 'A1' | 'A2L' | 'A3' | 'B2L'

export interface Refrigerant {
    id:string;
    name:string;
    aliases:string[];
    category:RefrigerantCategory;
    status:RegulatoryStatus;
    gwp:number;
    odp:number;
    safetyClass:SafetyClass;
    approvedSubstitutes:string[];
    summary:string;
    liquidDensityLbGal:number|null;
    defaultPricePerLb:number|null;
}

export const REFRIGERANTS:ReadonlyArray<Refrigerant> = [
    {
        id: 'R-22',
        name: 'R-22',
        aliases: ['HCFC-22', 'Freon 22', 'Genetron 22'],
        category: 'HCFC',
        status: 'Phased Out',
        gwp: 1760,
        odp: 0.055,
        safetyClass: 'A1',
        approvedSubstitutes: ['R-407C', 'R-448A', 'R-449A', 'R-422D', 'R-438A'],
        summary: 'Common HCFC refrigerant phased out of new equipment in 2010 ' +
            'and new production/import banned after 2020 under Section 608. ' +
            'Still in service in existing systems.',
        liquidDensityLbGal: 10.3,
        defaultPricePerLb: 20,
    },
    {
        id: 'R-410A',
        name: 'R-410A',
        aliases: ['Puron', 'AZ-20', 'Genetron AZ-20'],
        category: 'HFC Blend',
        status: 'Phasing Down',
        gwp: 2088,
        odp: 0,
        safetyClass: 'A1',
        approvedSubstitutes: ['R-32', 'R-452B', 'R-454B', 'R-466A'],
        summary: 'Dominant HFC blend for residential and light commercial AC. ' +
            'Production declining under the AIM Act GWP caps. ' +
            'New equipment phasing to lower-GWP alternatives.',
        liquidDensityLbGal: 9.6,
        defaultPricePerLb: 8,
    },
    {
        id: 'R-404A',
        name: 'R-404A',
        aliases: ['HP62', 'Forane 404A'],
        category: 'HFC Blend',
        status: 'Phasing Down',
        gwp: 3922,
        odp: 0,
        safetyClass: 'A1',
        approvedSubstitutes: ['R-448A', 'R-449A', 'R-452A', 'R-407A'],
        summary: 'High-GWP HFC blend used in commercial refrigeration. ' +
            'Subject to rapid phasedown under AIM Act. ' +
            'Being displaced by lower-GWP blends like R-448A and R-449A.',
        liquidDensityLbGal: 8.8,
        defaultPricePerLb: 12,
    },
    {
        id: 'R-134a',
        name: 'R-134a',
        aliases: ['HFC-134a', 'Suva 134a', 'Genetron 134a'],
        category: 'HFC',
        status: 'Phasing Down',
        gwp: 1430,
        odp: 0,
        safetyClass: 'A1',
        approvedSubstitutes: ['R-1234yf', 'R-513A', 'R-744'],
        summary: 'Single-component HFC used in automotive AC, chillers, and ' +
            'some refrigeration. Phasing down under AIM Act. ' +
            'Mobile AC transitioning to R-1234yf.',
        liquidDensityLbGal: 10.3,
        defaultPricePerLb: 6,
    },
    {
        id: 'R-407C',
        name: 'R-407C',
        aliases: ['Genetron 407C', 'Forane 407C'],
        category: 'HFC Blend',
        status: 'Phasing Down',
        gwp: 1774,
        odp: 0,
        safetyClass: 'A1',
        approvedSubstitutes: ['R-32', 'R-454B', 'R-448A'],
        summary: 'HFC blend widely used as an R-22 retrofit and in new ' +
            'equipment. Subject to AIM Act phasedown. ' +
            'Non-azeotropic blend with temperature glide.',
        liquidDensityLbGal: 9.6,
        defaultPricePerLb: 10,
    },
    {
        id: 'R-507A',
        name: 'R-507A',
        aliases: ['AZ-50', 'Genetron AZ-50'],
        category: 'HFC Blend',
        status: 'Phasing Down',
        gwp: 3985,
        odp: 0,
        safetyClass: 'A1',
        approvedSubstitutes: ['R-448A', 'R-449A', 'R-452A'],
        summary: 'Near-azeotropic HFC blend for low-temperature refrigeration. ' +
            'Very high GWP; subject to accelerated phasedown under AIM Act.',
        liquidDensityLbGal: 9.0,
        defaultPricePerLb: 15,
    },
    {
        id: 'R-452A',
        name: 'R-452A',
        aliases: ['Opteon XP44'],
        category: 'HFO/HFC Blend',
        status: 'Transitional',
        gwp: 2140,
        odp: 0,
        safetyClass: 'A1',
        approvedSubstitutes: ['R-448A', 'R-449A', 'R-454C'],
        summary: 'Transitional HFO/HFC blend used as an R-404A/R-507A retrofit ' +
            'in medium- and low-temperature refrigeration. ' +
            'Lower GWP than predecessors but still declining under AIM Act.',
        liquidDensityLbGal: 9.4,
        defaultPricePerLb: 12,
    },
    {
        id: 'R-448A',
        name: 'R-448A',
        aliases: ['Solstice N40', 'Genetron N40'],
        category: 'HFO/HFC Blend',
        status: 'Transitional',
        gwp: 1387,
        odp: 0,
        safetyClass: 'A1',
        approvedSubstitutes: ['R-449A', 'R-454C'],
        summary: 'EPA SNAP-approved HFO/HFC blend for commercial refrigeration ' +
            'as an R-22 and R-404A/R-507A retrofit. ' +
            'Significant GWP reduction vs. legacy blends.',
        liquidDensityLbGal: 9.4,
        defaultPricePerLb: 10,
    },
    {
        id: 'R-449A',
        name: 'R-449A',
        aliases: ['Opteon XP40'],
        category: 'HFO/HFC Blend',
        status: 'Transitional',
        gwp: 1397,
        odp: 0,
        safetyClass: 'A1',
        approvedSubstitutes: ['R-448A', 'R-454C'],
        summary: 'EPA SNAP-approved HFO/HFC blend for medium- and ' +
            'low-temperature commercial refrigeration. ' +
            'Similar GWP to R-448A; alternative R-404A/R-507A retrofit.',
        liquidDensityLbGal: 9.5,
        defaultPricePerLb: 10,
    },
    {
        id: 'R-513A',
        name: 'R-513A',
        aliases: ['Opteon XP10'],
        category: 'HFO/HFC Blend',
        status: 'Transitional',
        gwp: 631,
        odp: 0,
        safetyClass: 'A1',
        approvedSubstitutes: ['R-1234yf', 'R-744'],
        summary: 'EPA SNAP-approved HFO/HFC blend as an R-134a alternative ' +
            'in chillers and retail refrigeration. ' +
            'Significantly lower GWP than R-134a; A1 safety class.',
        liquidDensityLbGal: 9.3,
        defaultPricePerLb: 12,
    },
    {
        id: 'R-454B',
        name: 'R-454B',
        aliases: ['Opteon XL41', 'Puron Advance'],
        category: 'HFO/HFC Blend',
        status: 'Active',
        gwp: 467,
        odp: 0,
        safetyClass: 'A2L',
        approvedSubstitutes: [],
        summary: 'Primary next-generation replacement for R-410A in residential ' +
            'and light commercial AC under AIM Act GWP caps. ' +
            'A2L safety class requires updated equipment and technician training.',
        liquidDensityLbGal: 9.0,
        defaultPricePerLb: 15,
    },
    {
        id: 'R-32',
        name: 'R-32',
        aliases: ['HFC-32', 'Difluoromethane'],
        category: 'HFC',
        status: 'Active',
        gwp: 675,
        odp: 0,
        safetyClass: 'A2L',
        approvedSubstitutes: [],
        summary: 'Single-component HFC used widely in split systems globally ' +
            'and gaining adoption in the US as an R-410A alternative. ' +
            'A2L safety class; better efficiency and lower GWP than R-410A.',
        liquidDensityLbGal: 7.4,
        defaultPricePerLb: 5,
    },
    {
        id: 'R-290',
        name: 'R-290',
        aliases: ['Propane', 'HC-290'],
        category: 'Natural',
        status: 'Future-Proof',
        gwp: 3,
        odp: 0,
        safetyClass: 'A3',
        approvedSubstitutes: [],
        summary: 'Hydrocarbon refrigerant with near-zero GWP. ' +
            'Highly flammable (A3) ' + EM_DASH + ' limited to small-charge sealed systems ' +
            'and specialized applications. Excellent thermodynamic properties.',
        liquidDensityLbGal: 4.1,
        defaultPricePerLb: null,
    },
    {
        id: 'R-717',
        name: 'R-717',
        aliases: ['Ammonia', 'NH3'],
        category: 'Natural',
        status: 'Future-Proof',
        gwp: 0,
        odp: 0,
        safetyClass: 'B2L',
        approvedSubstitutes: [],
        summary: 'Ammonia is a natural refrigerant with zero GWP and excellent ' +
            'thermodynamic efficiency. Toxicity (B) and flammability (2L) ' +
            'restrict use to industrial and large commercial systems.',
        liquidDensityLbGal: 5.1,
        defaultPricePerLb: null,
    },
    {
        id: 'R-744',
        name: 'R-744',
        aliases: ['CO2', 'Carbon Dioxide'],
        category: 'Natural',
        status: 'Future-Proof',
        gwp: 1,
        odp: 0,
        safetyClass: 'A1',
        approvedSubstitutes: [],
        summary: 'CO2 refrigerant with GWP of 1 used in transcritical systems ' +
            'for supermarket refrigeration and heat pumps. ' +
            'Requires high-pressure equipment design. Growing adoption globally.',
        liquidDensityLbGal: null,
        defaultPricePerLb: null,
    },
    {
        id: 'R-1234yf',
        name: 'R-1234yf',
        aliases: ['HFO-1234yf', 'Opteon YF'],
        category: 'HFO',
        status: 'Future-Proof',
        gwp: 4,
        odp: 0,
        safetyClass: 'A2L',
        approvedSubstitutes: [],
        summary: 'Low-GWP HFO refrigerant now standard in automotive AC. ' +
            'Also used in some stationary equipment as an R-134a replacement. ' +
            'A2L; mildly flammable but widely approved.',
        liquidDensityLbGal: 8.7,
        defaultPricePerLb: 25,
    },
    {
        id: 'R-1234ze',
        name: 'R-1234ze',
        aliases: ['HFO-1234ze', 'Solstice ze'],
        category: 'HFO',
        status: 'Future-Proof',
        gwp: 7,
        odp: 0,
        safetyClass: 'A2L',
        approvedSubstitutes: [],
        summary: 'Low-GWP HFO used in large chillers and low-pressure ' +
            'centrifugal applications as an R-134a alternative. ' +
            'A2L safety class; used in data centers and commercial buildings.',
        liquidDensityLbGal: 8.7,
        defaultPricePerLb: null,
    },
    {
        id: 'R-600a',
        name: 'R-600a',
        aliases: ['Isobutane', 'HC-600a'],
        category: 'Natural',
        status: 'Future-Proof',
        gwp: 3,
        odp: 0,
        safetyClass: 'A3',
        approvedSubstitutes: [],
        summary: 'Hydrocarbon refrigerant used globally in domestic ' +
            'refrigerators and small appliances. Near-zero GWP. ' +
            'Highly flammable (A3) ' + EM_DASH + ' limited to small sealed factory-charged systems.',
        liquidDensityLbGal: 4.5,
        defaultPricePerLb: null,
    },
] as const
